// 사용량 — 최근 몇 달의 견적 발송·동시 현장·고객 알림·사진·활동 멤버(운영자 스크립트 `org-usage` 와 같은 집계) · 요금제 카드의 「진행 중 현장」(시공중만)과 낱말을 겹치지 않는다.

import Banner from "@buildpy/ui/banner";
import ResponsiveList, { ListCard } from "@/components/responsive-list";
import SectionCard from "@buildpy/ui/section-card";
import ListTable from "@buildpy/ui/list-table";
import { TableCell, TableRow } from "@buildpy/ui/table";
import { formatNumber } from "@buildpy/core/lib/format";
import { formatBytes, type OrgUsage, type OrgUsageMonth, USAGE_MONTHS } from "@buildpy/core/modules/plan/usage-model";

interface Props {
  // null = 조회 실패(페이지는 살리고 이 카드만 안내).
  usage: OrgUsage | null;
}

export const USAGE_LOAD_ERROR = "사용량을 불러오지 못했습니다. 잠시 뒤 새로고침해 주세요.";

const monthLabel = (month: string): string => {
  const [y, m] = month.split("-");
  return `${y}년 ${Number(m)}월`;
};

const CELL = "whitespace-nowrap px-3 py-2 text-right tabular-nums";

// 열 이름 한 벌 — 표 머리와 폰 카드가 같은 낱말을 쓴다.
const COL = {
  sends: "견적 발송",
  sites: "동시 최대(착공 전 포함)",
  notify: "고객 알림 발송 · 기록만",
  photos: "새 사진",
  members: "활동 멤버",
} as const;

const cellsOf = (row: OrgUsageMonth) => ({
  sends: `${formatNumber(row.quoteSends)}건`,
  sites: `${formatNumber(row.siteMax)}곳`,
  notify: `${formatNumber(row.notify.sent)} · ${formatNumber(row.notify.logOnly)}`,
  photos: `${formatNumber(row.photoCount)}장 · ${formatBytes(row.photoBytes)}`,
  members: `${formatNumber(row.activeMembers)}명`,
});

const MonthName = ({ row, current }: { row: OrgUsageMonth; current: boolean }) => (
  <>
    {monthLabel(row.month)}
    {current && <span className="ml-1 text-xs text-muted-foreground">(이번 달)</span>}
  </>
);

const UsageRow = ({ row, current }: { row: OrgUsageMonth; current: boolean }) => {
  const c = cellsOf(row);
  return (
    <TableRow data-testid={`settings-usage-row-${row.month}`}>
      <TableCell className="whitespace-nowrap px-3 py-2 font-medium">
        <MonthName current={current} row={row} />
      </TableCell>
      <TableCell className={CELL} data-testid="settings-usage-sends">
        {c.sends}
      </TableCell>
      <TableCell className={CELL} data-testid="settings-usage-sites">
        {c.sites}
      </TableCell>
      <TableCell className={CELL} data-testid="settings-usage-notify">
        {c.notify}
      </TableCell>
      <TableCell className={CELL} data-testid="settings-usage-photos">
        {c.photos}
      </TableCell>
      <TableCell className={CELL} data-testid="settings-usage-members">
        {c.members}
      </TableCell>
    </TableRow>
  );
};

const UsageCard = ({ row, current }: { row: OrgUsageMonth; current: boolean }) => {
  const c = cellsOf(row);
  return (
    <ListCard testId={`settings-usage-card-${row.month}`}>
      <p className="font-medium">
        <MonthName current={current} row={row} />
      </p>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        {(Object.keys(COL) as Array<keyof typeof COL>).map((key) => (
          <div className="min-w-0" key={key}>
            <dt className="text-muted-foreground">{COL[key]}</dt>
            <dd className="tabular-nums">{c[key]}</dd>
          </div>
        ))}
      </dl>
    </ListCard>
  );
};

const SettingsUsage = ({ usage }: Props) => {
  const count = usage?.months.length ?? USAGE_MONTHS;
  return (
    <SectionCard
      data-testid="settings-usage"
      description={
        usage ? `이번 달이 맨 위 · 지금 착공 전·시공중 현장 ${formatNumber(usage.sitesNow)}곳` : undefined
      }
      title={`최근 ${count}개월 사용량`}
    >
      {usage ? (
        <>
          <ResponsiveList
            cards={usage.months.map((row, i) => (
              <UsageCard current={i === 0} key={row.month} row={row} />
            ))}
            label="달별 사용량"
            table={
              <ListTable
                columns={[
                  { label: "월" },
                  { label: COL.sends, align: "right" },
                  { label: COL.sites, align: "right" },
                  { label: COL.notify, align: "right" },
                  { label: COL.photos, align: "right" },
                  { label: COL.members, align: "right" },
                ]}>
                {usage.months.map((row, i) => (
                  <UsageRow current={i === 0} key={row.month} row={row} />
                ))}
              </ListTable>
            }
            testId="settings-usage-cards"
          />
          <p className="mt-2 text-xs text-muted-foreground">
            견적 발송은 판(리비전)마다 한 건 · 동시 최대는 그 달에 착공 전·시공중이던 현장이 한꺼번에 가장 많았던 수 · 활동
            멤버는 그 달에 기록을 남기거나 사진을 올린 직원입니다.
          </p>
        </>
      ) : (
        <Banner tone="warning">{USAGE_LOAD_ERROR}</Banner>
      )}
    </SectionCard>
  );
};

export default SettingsUsage;
